

























import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import ChevronRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import SkipNext from 'vue-material-design-icons/SkipNext.vue';
import SkipPrevious from 'vue-material-design-icons/SkipPrevious.vue';

export interface Page {
	start: number;
	end: number;
}

@Component({
	components: {
		ChevronRight,
		ChevronLeft,
		SkipNext,
		SkipPrevious
	}
})
export default class PagingControl extends Vue {
	@Prop({default: 0}) public total: number;
	@Prop({default: 50}) public rowsPerPage: number;
	@Prop({default: 1}) public page: number;
	@Prop({default: false}) public disableControls: boolean;

	public currentPage = 1;
	public totalRows = 0;

	public mounted() {
		this.totalRows = this.total || 0;
		this.currentPage = this.page || 1;
	}

	@Watch('total')
	public onTotalChanged(current: number, previous: number) {
		this.totalRows = current;

		if (this.totalRows < 0) {
			this.totalRows = 0;
		}

		// if new total means that our current page is invalid, then reset back to the first page
		if (this.currentPage > 1 && this.isPageOutOfBounds(this.currentPage)) {
			this.currentPage = 1;
			this.pageChanged();
		}
	}

	public numberOfPages() {
		if (this.totalRows <= 0) {
			return 1;
		}

		return Math.max(Math.ceil(this.totalRows / this.rowsPerPage), 1);
	}

	public last() {
		if (this.disableControls) {
			return;
		}

		if (this.hasNext()) {
			this.currentPage = this.numberOfPages();
			this.pageChanged();
		}
	}

	public first() {
		if (this.disableControls) {
			return;
		}

		if (this.hasPrevious()) {
			this.currentPage = 1;
			this.pageChanged();
		}
	}

	public next() {
		if (this.disableControls) {
			return;
		}

		if (this.hasNext()) {
			this.currentPage++;
			this.pageChanged();
		}
	}

	public previous() {
		if (this.disableControls) {
			return;
		}

		if (this.hasPrevious()) {
			this.currentPage--;
			this.pageChanged();
		}
	}

	public hasNext() {
		const next = this.currentPage + 1;
		return !this.isPageOutOfBounds(next);
	}

	public hasPrevious() {
		return this.currentPage > 1;
	}

	private startOfPage(pageNumber?: number): number {
		// default to current page if page number not supplied
		pageNumber = typeof pageNumber !== 'undefined' ? pageNumber : this.currentPage;
		return (pageNumber - 1) * this.rowsPerPage + 1;
	}

	private endOfPage(pageNumber?: number): number {
		// default to current page if page number not supplied
		pageNumber = typeof pageNumber !== 'undefined' ? pageNumber : this.currentPage;
		return Math.min(pageNumber * this.rowsPerPage, this.totalRows);
	}

	private pageChanged() {
		// our rows are 1-based, but consumers of this will likely have their records stored in 0-based arrays
		this.$emit('page-changed', {
			start: this.startOfPage() - 1,
			end: this.endOfPage()
		} as Page);
	}

	private isPageOutOfBounds(pageNumber: number): boolean {
		return this.startOfPage(pageNumber) > this.totalRows;
	}
}
